import React, { useEffect, useRef } from 'react';
import type { RfqListItemDto } from '../../lib/rfqApi';
import { RfqListItem } from './RfqListItem';

interface Props {
  rfqs: RfqListItemDto[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  total?: number;
}

export const RfqList: React.FC<Props> = ({
  rfqs,
  selectedId,
  onSelect,
  onDelete,
  onLoadMore,
  hasMore,
  loadingMore,
  total,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Trigger load more when user scrolls near bottom
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const thresholdPx = 180;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (distanceFromBottom < thresholdPx) {
      if (hasMore && !loadingMore) {
        onLoadMore();
      }
    }
  };

  // If list is short and container has room, auto-load more until it fills (nice UX)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const canScroll = el.scrollHeight > el.clientHeight + 8;
    if (!canScroll && hasMore && !loadingMore) {
      onLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqs.length, hasMore]);

  if (!rfqs.length) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <div className="text-center text-xs text-slate-500">
          <div className="mb-1 font-semibold text-slate-600">No RFQs found</div>
          <div className="text-xs text-slate-400">
            Try changing the status filter or create a new RFQ.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100"
      >
        {rfqs.map((rfq) => (
          <RfqListItem
            key={rfq._id}
            rfq={rfq}
            selected={selectedId === rfq._id}
            onClick={() => onSelect(rfq._id)}
            onDelete={onDelete}
          />
        ))}

        {/* Load-more footer */}
        <div className="px-4 py-3 text-center">
          {loadingMore ? (
            <div className="text-xs text-slate-500">Loading more…</div>
          ) : hasMore ? (
            <button
              type="button"
              onClick={onLoadMore}
              className="text-xs font-semibold text-primary-500 hover:underline"
            >
              Load more
            </button>
          ) : (
            <div className="text-xs text-slate-400">End of list</div>
          )}
        </div>
      </div>
    </div>
  );
};
